import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import { IndeterminateCheckbox } from "components/intermediateCheckbox";
import { Radio } from "components/radio";
import { Table } from "components/table";
import currency from "currency.js";
import AchFields, { AchFieldsSchema } from "features/paymentDetailsForm/fields/achFields";
import {
  AmountFields,
  getAmountFieldsSchema,
} from "features/paymentDetailsForm/fields/amountFields";
import CreditCardFields, {
  CreditCardFieldsSchema,
} from "features/paymentDetailsForm/fields/creditCardFields";
import PersonalDetailsFields, {
  PersonalDetailsSchema,
} from "features/paymentDetailsForm/fields/personalDetailsFields";
import { Field, Form, Formik, useFormikContext } from "formik";
import { useEffect, useMemo, useState } from "react";
import { LiaCreditCardSolid, LiaMoneyCheckAltSolid } from "react-icons/lia";
import { formatCurrency } from "utils/currencyUtils";
import * as Yup from "yup";

// The Pay button sits well below the amount and card fields, so a failed submit used
// to look like nothing happened — the error was off-screen. Bring the first invalid
// field into view and focus it. Chakra puts aria-invalid on the inputs of any
// FormControl marked isInvalid, so this covers every field without listing them.
function ScrollToFirstError() {
  const { submitCount, isValid, isSubmitting } = useFormikContext();

  useEffect(() => {
    // isSubmitting drops back to false once a failed validation has been applied to
    // the DOM; running before that would query for aria-invalid too early.
    if (submitCount === 0 || isValid || isSubmitting) return undefined;
    const frame = requestAnimationFrame(() => {
      const firstInvalid = document.querySelector('[aria-invalid="true"]');
      if (!firstInvalid) return;
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
      firstInvalid.focus?.({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [submitCount, isValid, isSubmitting]);

  return null;
}

const paymentMethodOptions = [
  {
    label: (
      <HStack>
        <Icon as={LiaCreditCardSolid} boxSize={4} />
        <Text>Credit Card</Text>
      </HStack>
    ),
    value: "credit_card",
  },
  {
    label: (
      <HStack>
        <Icon as={LiaMoneyCheckAltSolid} boxSize={4} />
        <Text>ACH</Text>
      </HStack>
    ),
    value: "ach",
  },
];

const paymentColumns = [
  {
    id: "select",
    header: ({ table }) => (
      <IndeterminateCheckbox
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }) => (
      <div className="px-1">
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      </div>
    ),
  },
  {
    header: "Account #",
    accessorKey: "externalId",
  },
  {
    header: "Account Balance",
    accessorKey: "debtAmount",
    cell: (info) => formatCurrency(info.getValue()),
  },
  {
    header: "Original Creditor Name",
    accessorKey: "originalCreditorName",
  },
];

export default function PaymentDetailsForm({
  userData,
  paymentRules,
  onSubmit,
  isLoading,
  convenienceFee = 0,
}) {
  const payableAccounts = useMemo(
    () =>
      userData?.accounts
        ?.filter((account) => !(account.isSettled || account.isClosed))
        ?.reduce((acc, debtorAccount) => {
          const debtAmount = currency(debtorAccount.totalBalance, { precision: 4 }).value;
          if (debtAmount > 0) {
            return [
              ...acc,
              {
                externalId: debtorAccount.externalId,
                debtAmount,
                originalCreditorName: debtorAccount.originalCreditorName,
              },
            ];
          }
          return [...acc];
        }, []) ?? [],
    [userData],
  );
  const [isDisclosureOpen, setIsDisclosureOpen] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);
  const [rowSelection, setRowSelection] = useState(
    payableAccounts?.reduce((acc, debtorAccount) => {
      acc[debtorAccount.externalId] = true;
      return acc;
    }, {}),
  );
  const selectedAccounts = useMemo(() => {
    return payableAccounts?.filter((account) => rowSelection[account.externalId]) ?? [];
  }, [payableAccounts, rowSelection]);
  const debtTotal = useMemo(
    () =>
      currency(
        selectedAccounts?.reduce((accumulator, account) => {
          return currency(accumulator, { precision: 4 }).add(account?.debtAmount ?? 0);
        }, 0),
        { precision: 2 },
      ).value,
    [selectedAccounts],
  );
  const minimumByExternalId = useMemo(
    () =>
      Object.fromEntries(
        (paymentRules ?? []).map((rule) => [
          rule.externalId,
          currency(rule.minimumPaymentAmount ?? 0, { precision: 4 }).value,
        ]),
      ),
    [paymentRules],
  );
  // AKT-8286: the creditor's minimum payment rule, resolved per account. Paying
  // several accounts at once takes the least restrictive minimum. Accounts with no
  // rule configured report 0, so the historical $1 floor stays in place for them.
  const minimumAmount = useMemo(() => {
    if (!selectedAccounts.length) return 1;
    const lowest = Math.min(
      ...selectedAccounts.map((account) => minimumByExternalId[account.externalId] ?? 0),
    );
    return lowest > 0 ? lowest : 1;
  }, [selectedAccounts, minimumByExternalId]);
  const PaymentDetailsSchema = Yup.object()
    .shape({
      selectedPaymentMethod: Yup.string().oneOf(["credit_card", "ach"]).required(),
      paymentMethod: Yup.object().shape({
        emails: Yup.string().email(),
      }),
    })
    // We add all the schemas from the other field groups
    .concat(getAmountFieldsSchema({ totalAmount: debtTotal, minimumAmount }))
    .concat(PersonalDetailsSchema)
    .concat(CreditCardFieldsSchema)
    .concat(AchFieldsSchema);

  const handleOnSubmit = (values, formikHelpers) => {
    const payload = {
      ...values,
      externalAccountIds: selectedAccounts.map((account) => account.externalId),
    };
    formikHelpers.setSubmitting(false);
    if (convenienceFee <= 0) {
      onSubmit(payload, formikHelpers);
      return;
    }
    setPendingPayment({ payload, formikHelpers });
    setHasAcknowledged(false);
    setIsDisclosureOpen(true);
  };

  const handleProceed = () => {
    if (!pendingPayment) return;
    const { payload, formikHelpers } = pendingPayment;
    setIsDisclosureOpen(false);
    setPendingPayment(null);
    onSubmit(payload, formikHelpers);
  };

  const handleCloseDisclosure = () => {
    setIsDisclosureOpen(false);
    setPendingPayment(null);
  };

  return (
    <>
      <Formik
        initialValues={{
          selectedPaymentMethod: "credit_card",
          selectedPaymentAmount: "full_amount",
          paymentMethod: {
            state: "AL",
          },
          paymentIntent: {
            totalAmount: debtTotal,
          },
        }}
        onSubmit={handleOnSubmit}
        validationSchema={PaymentDetailsSchema}
      >
        {(props) => (
          // noValidate: the Yup schema already marks every required field, and the
          // browser's own validation would otherwise pop a native tooltip and steal
          // focus before the inline errors below can be shown.
          <Form noValidate>
            <ScrollToFirstError />
            <VStack spacing={1}>
              <Heading size="md" fontWeight="medium" mb={2}>
                Select Accounts for Payment
              </Heading>
              <Table
                columns={paymentColumns}
                data={payableAccounts}
                enableMultiRowSelection
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                getRowId={(row) => row.externalId}
              />
            </VStack>
            <AmountFields totalAmount={debtTotal} minimumAmount={minimumAmount} />
            <Spacer my={6} />
            <Field name="selectedPaymentMethod">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    form.errors.selectedPaymentMethod && form.touched.selectedPaymentMethod
                  }
                >
                  <FormLabel>Payment Method</FormLabel>
                  <Radio form={form} field={field} options={paymentMethodOptions} />
                </FormControl>
              )}
            </Field>
            <Spacer my={12} />
            <Heading size="sm">Payment Information</Heading>
            {props.values.selectedPaymentMethod === "credit_card" && <CreditCardFields />}
            {props.values.selectedPaymentMethod === "ach" && <AchFields />}
            <Heading size="sm">Billing Details</Heading>
            <VStack>
              <PersonalDetailsFields />
              <Field name="paymentMethod.emails">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.paymentMethod?.emails && form.touched.paymentMethod?.emails
                    }
                  >
                    <Spacer my={6} />
                    <FormLabel>Email Address</FormLabel>
                    <Input {...field} type="email" />
                    <FormErrorMessage>{form.errors.paymentMethod?.emails}</FormErrorMessage>
                    <Spacer my={6} />
                    <FormHelperText textAlign="justify">
                      By entering your email address in our payment portal, you acknowledge and
                      consent to communications via email. These communications may include
                      transactional information, updates, and other relevant notifications. Your
                      email will not be shared with third parties without your explicit permission.
                    </FormHelperText>
                  </FormControl>
                )}
              </Field>
              {props.status && (
                <Alert status="error" my={[2, 2, 4]}>
                  <AlertIcon />
                  {props.status}
                </Alert>
              )}
              <Button
                colorScheme="blue"
                type="submit"
                w="180px"
                h="50px"
                mb={[8, 8, 4]}
                isLoading={props.isSubmitting || isLoading}
                isDisabled={selectedAccounts?.length === 0}
              >
                Pay
              </Button>
              <Text mb={[8, 8, 4]} fontSize="sm" fontStyle="italic" color="gray.500">
                This communication is from a debt collector. This is an attempt to collect a debt.
                Any information obtained will be used for that purpose. Making a payment today does
                not overshadow the rights provided to you in your initial notice.
              </Text>
            </VStack>
          </Form>
        )}
      </Formik>
      <Modal isOpen={isDisclosureOpen} onClose={handleCloseDisclosure} isCentered>
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>Convenience Fee Disclosure</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text textAlign="justify" mb={4}>
              Attention: You are authorizing the payment service provider to charge your payment
              method for the principal amount and a convenience fee. For ACH payments, this will
              appear on your statement as two separate transactions. For card payments, this will
              appear as one transaction. You understand that you are paying a fee for the
              convenience of making this payment by phone and that you can avoid this fee by mailing
              a check or money order to our office.
            </Text>
            <Text fontWeight="bold" mb={4}>
              Convenience Fee: {formatCurrency(convenienceFee)}
            </Text>
            <Checkbox
              isChecked={hasAcknowledged}
              onChange={(e) => setHasAcknowledged(e.target.checked)}
            >
              By checking this box, you are acknowledging and agreeing to our terms and conditions
            </Checkbox>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={handleCloseDisclosure}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isDisabled={!hasAcknowledged}
              isLoading={isLoading}
              onClick={handleProceed}
            >
              Proceed with payment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
